import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";
import Loader from "../../dist/Loader";
import { formatDate } from "../../utils/formatDate";

const ExpenseFf = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);

  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [financialYear, setFinancialYear] = useState("");
  const [financialYearOptions, setFinancialYearOptions] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  const generateFinancialYears = () => {
    const startYear = 2021;
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const fyList = [];
    for (let year = startYear; year <= currentYear; year++) {
      fyList.push(`${year}-${year + 1}`);
    }

    const defaultFY =
      currentMonth >= 4
        ? `${currentYear}-${currentYear + 1}`
        : `${currentYear - 1}-${currentYear}`;

    setFinancialYearOptions(fyList);
    setFinancialYear(defaultFY);
  };

  useEffect(() => {
    generateFinancialYears();
  }, []);

  // fetch divisions helper (supports "All")
  const fetchDivisions = useCallback(async (stateId) => {
    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All") {
        url += `?state_id=${stateId}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions([{ id: "All", name: "All" }, ...res.data]);
      setSelectedDivision("All");
    } catch {
      setDivisions([{ id: "All", name: "All" }]);
      setSelectedDivision("All");
    }
  }, []);

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = [];
      if (stateId && stateId !== "All") params.push(`state_id=${stateId}`);
      if (divisionName && divisionName !== "All")
        params.push(`division=${encodeURIComponent(divisionName)}`);
      if (params.length) url += `?${params.join("&")}`;

      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ]);
      setSelectedEmployee("All");
    } catch {
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      setSelectedEmployee("All");
    }
  }, []);

  // On mount: load states list only. Do not auto-fetch divisions/employees or table data.
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        // prepend All option
        setStates([{ id: "All", name: "All" }, ...response.data]);
        setSelectedState("All");
        // leave divisions and employees as default All lists without calling their APIs
      } catch {
        setError("Failed to load states");
      }
    };

    fetchStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setShowContainers(false);

    // when user changes state, fetch divisions and employees for that state
    await fetchDivisions(stateId);
    await fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setShowContainers(false);

    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.name || "All";

    // fetch employees filtered by selectedState and divisionName
    await fetchEmployees(selectedState, divisionName);
  };

  const fetchData = useCallback(async () => {
    // allow selectedState === "All" but block empty string
    if (selectedState === "") return;

    try {
      setIsLoading(true);
      setError(null);

      let url = `${BASE_URL}/api/pob-targets/pob_target_report?`;

      // append params only when not "All"
      if (selectedState && selectedState !== "All")
        url += `state_id=${selectedState}&`;
      if (selectedDivision !== "All") {
        const division = divisions.find((d) => d.id == selectedDivision);
        if (division?.name)
          url += `division=${encodeURIComponent(division.name)}&`;
      }
      if (selectedEmployee !== "All") url += `employee_id=${selectedEmployee}&`;
      if (financialYear) url += `financial_year=${financialYear}&`;
      if (searchQuery) url += `search=${searchQuery}&`;

      url = url.replace(/&$/, "");

      const response = await axios.get(url, { headers: getAuthHeaders() });

      if (Array.isArray(response.data)) setTableData(response.data);
      else if (Array.isArray(response.data.data))
        setTableData(response.data.data);
      else setTableData([]);

      setShowContainers(true);
    } catch (err) {
      setError("Failed to fetch data");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState,
    selectedDivision,
    selectedEmployee,
    financialYear,
    searchQuery,
    divisions,
  ]);

  /**
   * Helper: compute totals for month columns and total pob
   */
  const computeTotals = () => {
    const totals = {
      apr: 0,
      may: 0,
      jun: 0,
      jul: 0,
      aug: 0,
      sep: 0,
      oct: 0,
      nov: 0,
      dec: 0,
      jan: 0,
      feb: 0,
      mar: 0,
      pobTotal: 0,
    };

    tableData.forEach((r) => {
      totals.apr +=
        Number(
          r.apr_pob_achievement || r.apr_pob_achievement === 0
            ? r.apr_pob_achievement
            : 0,
        ) || 0;
      totals.may += Number(r.may_pob || r.may_pob === 0 ? r.may_pob : 0) || 0;
      totals.jun +=
        Number(
          r.jun_pob_achievement || r.jun_pob_achievement === 0
            ? r.jun_pob_achievement
            : 0,
        ) || 0;
      totals.jul +=
        Number(
          r.jul_pob_achievement || r.jul_pob_achievement === 0
            ? r.jul_pob_achievement
            : 0,
        ) || 0;
      totals.aug +=
        Number(
          r.aug_pob_achievement || r.aug_pob_achievement === 0
            ? r.aug_pob_achievement
            : 0,
        ) || 0;
      totals.sep +=
        Number(
          r.sep_pob_achievement || r.sep_pob_achievement === 0
            ? r.sep_pob_achievement
            : 0,
        ) || 0;
      totals.oct +=
        Number(
          r.oct_pob_achievement || r.oct_pob_achievement === 0
            ? r.oct_pob_achievement
            : 0,
        ) || 0;
      totals.nov +=
        Number(
          r.nov_pob_achievement || r.nov_pob_achievement === 0
            ? r.nov_pob_achievement
            : 0,
        ) || 0;
      totals.dec +=
        Number(
          r.dec_pob_achievement || r.dec_pob_achievement === 0
            ? r.dec_pob_achievement
            : 0,
        ) || 0;
      totals.jan +=
        Number(
          r.jan_pob_achievement || r.jan_pob_achievement === 0
            ? r.jan_pob_achievement
            : 0,
        ) || 0;
      totals.feb +=
        Number(
          r.feb_pob_achievement || r.feb_pob_achievement === 0
            ? r.feb_pob_achievement
            : 0,
        ) || 0;
      totals.mar +=
        Number(
          r.mar_pob_achievement || r.mar_pob_achievement === 0
            ? r.mar_pob_achievement
            : 0,
        ) || 0;
      totals.pobTotal += Number(r.pob || r.pob === 0 ? r.pob : 0) || 0;
    });

    return totals;
  };

  /**
   * Export tableData to CSV which opens fine in Excel.
   */
  const handleExportExcel = () => {
    if (!tableData || tableData.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "S.No",
      "Emp ID",
      "State",
      "Employee Name",
      "Designation",
      "HQ",
      "Division",
      "DOJ",
      "DOR",
      "Manager",
      "Apr POB",
      "May POB",
      "Jun POB",
      "Jul POB",
      "Aug POB",
      "Sep POB",
      "Oct POB",
      "Nov POB",
      "Dec POB",
      "Jan POB",
      "Feb POB",
      "Mar POB",
      "Total POB",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    tableData.forEach((r, idx) => {
      const row = [
        idx + 1,
        `"${(r.employee || "").toString().replace(/"/g, '""')}"`,
        `"${(r.state || "").toString().replace(/"/g, '""')}"`,
        `"${(r.employee_name || "").toString().replace(/"/g, '""')}"`,
        `"${(r.designation || "").toString().replace(/"/g, '""')}"`,
        `"${(r.headquarter || "").toString().replace(/"/g, '""')}"`,
        `"${(r.division || "").toString().replace(/"/g, '""')}"`,
        `"${(r.final_joining_date ? formatDate(r.final_joining_date) : "-").toString().replace(/"/g, '""')}"`,
        `"${(r.resign_date ? formatDate(r.resign_date) : "-").toString().replace(/"/g, '""')}"`,
        `"${(r.manager || "-").toString().replace(/"/g, '""')}"`,
        r.apr_pob_achievement || 0,
        r.may_pob || 0,
        r.jun_pob_achievement || 0,
        r.jul_pob_achievement || 0,
        r.aug_pob_achievement || 0,
        r.sep_pob_achievement || 0,
        r.oct_pob_achievement || 0,
        r.nov_pob_achievement || 0,
        r.dec_pob_achievement || 0,
        r.jan_pob_achievement || 0,
        r.feb_pob_achievement || 0,
        r.mar_pob_achievement || 0,
        r.pob || 0,
      ];
      csvRows.push(row.join(","));
    });

    const t = computeTotals();
    const totalsRow = [
      "",
      "Totals",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      t.apr.toFixed(2),
      t.may.toFixed(2),
      t.jun.toFixed(2),
      t.jul.toFixed(2),
      t.aug.toFixed(2),
      t.sep.toFixed(2),
      t.oct.toFixed(2),
      t.nov.toFixed(2),
      t.dec.toFixed(2),
      t.jan.toFixed(2),
      t.feb.toFixed(2),
      t.mar.toFixed(2),
      t.pobTotal.toFixed(2),
    ];
    csvRows.push(totalsRow.join(","));

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName = `pob_report_${selectedState || "report"}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Opens a new window containing a print-friendly table and triggers print.
   */
  const downloadAndPrintTableSheet = () => {
    if (!tableData || tableData.length === 0) {
      alert("No data to print");
      return;
    }

    const t = computeTotals();

    const style = `
      table { width: 100%; border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; font-size:12px; }
      th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
      th { background: #f3f3f3; }
      tfoot td { font-weight: bold; }
      h3 { font-family: Arial, Helvetica, sans-serif; }
    `;

    const headerRow = `
      <tr>
        <th>S.No</th><th>Emp ID</th><th>State</th><th>Employee Name</th><th>Designation</th><th>HQ</th><th>Division</th>
        <th>DOJ</th><th>DOR</th><th>Manager</th><th>Apr POB</th><th>May POB</th><th>Jun POB</th><th>Jul POB</th>
        <th>Aug POB</th><th>Sep POB</th><th>Oct POB</th><th>Nov POB</th><th>Dec POB</th><th>Jan POB</th><th>Feb POB</th>
        <th>Mar POB</th><th>Total POB</th>
      </tr>
    `;

    const bodyRows = tableData
      .map(
        (r, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${r.employee_id || ""}</td>
        <td>${r.state || ""}</td>
        <td>${r.employee_name || ""}</td>
        <td>${r.designation || ""}</td>
        <td>${r.headquarter || ""}</td>
        <td>${r.division || ""}</td>
        <td>${r.final_joining_date || "-"}</td>
        <td>${r.resign_date || "-"}</td>
        <td>${r.manager || "-"}</td>
        <td style="text-align:right">${Number(
          r.apr_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(r.may_pob || 0).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.jun_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.jul_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.aug_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.sep_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.oct_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.nov_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.dec_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.jan_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.feb_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(
          r.mar_pob_achievement || 0,
        ).toFixed(2)}</td>
        <td style="text-align:right">${Number(r.pob || 0).toFixed(2)}</td>
      </tr>
    `,
      )
      .join("");

    const totalsRowHtml = `
      <tr>
        <td colspan="10" style="text-align:right">Total</td>
        <td style="text-align:right">${t.apr.toFixed(2)}</td>
        <td style="text-align:right">${t.may.toFixed(2)}</td>
        <td style="text-align:right">${t.jun.toFixed(2)}</td>
        <td style="text-align:right">${t.jul.toFixed(2)}</td>
        <td style="text-align:right">${t.aug.toFixed(2)}</td>
        <td style="text-align:right">${t.sep.toFixed(2)}</td>
        <td style="text-align:right">${t.oct.toFixed(2)}</td>
        <td style="text-align:right">${t.nov.toFixed(2)}</td>
        <td style="text-align:right">${t.dec.toFixed(2)}</td>
        <td style="text-align:right">${t.jan.toFixed(2)}</td>
        <td style="text-align:right">${t.feb.toFixed(2)}</td>
        <td style="text-align:right">${t.mar.toFixed(2)}</td>
        <td style="text-align:right">${t.pobTotal.toFixed(2)}</td>
      </tr>
    `;

    const newWindow = window.open("", "_blank");
    if (!newWindow) {
      alert("Popup blocked. Please allow popups for this site.");
      return;
    }

    newWindow.document.write(`
      <html>
        <head>
          <title>POB Report - ${selectedState || ""}</title>
          <style>${style}</style>
        </head>
        <body>
          <h3>POB Report - ${selectedState || ""} - ${financialYear || ""}</h3>
          <table>
            <thead>${headerRow}</thead>
            <tbody>${bodyRows}</tbody>
            <tfoot>${totalsRowHtml}</tfoot>
          </table>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <Loader onCancel={() => setIsLoading(false)} />
        </div>
      )}

      <div className={`${isLoading ? "blur-sm pointer-events-none" : ""}`}>
        <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
          <h1 className="text-2xl font-bold text-center text-gray-800">
            <span className="text-blue-700">Expense </span>
            <span className="text-blue-500">FY</span>
          </h1>

          {/* Filters */}
          <div className="grid grid-cols-5 gap-4 items-center">
            <label className="text-right whitespace-nowrap">State</label>
            <select
              className="border rounded p-2"
              value={selectedState}
              onChange={(e) => handleStateChange(e.target.value)}
            >
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            <label className="text-right whitespace-nowrap">Division</label>
            <select
              className="border rounded p-2"
              value={selectedDivision}
              onChange={(e) => handleDivisionChange(e.target.value)}
            >
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-5 gap-4 items-center mt-2">
            <label className="text-right whitespace-nowrap">Employee</label>
            <select
              className="border rounded p-2"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.id === "All"
                    ? "All"
                    : `${emp.first_name} ${emp.last_name} ${emp.employee} | ${emp.designation || "-"} | ${emp.division || "-"} | ${emp.state_name || "-"}`}
                </option>
              ))}
            </select>

            <label className="text-right whitespace-nowrap">
              Financial Year
            </label>
            <select
              className="border rounded p-2"
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
            >
              {financialYearOptions.map((fy, idx) => (
                <option key={idx} value={fy}>
                  {fy}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-center">
            <CustomButton variant="go" onClick={fetchData}>
              GO
            </CustomButton>
          </div>

          {error && (
            <div className="text-red-600 mt-2">
              {error}{" "}
              <button onClick={fetchData} className="text-blue-600 ml-2">
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        {showContainers && (
          <div className="border rounded shadow  table-wrapper mt-5">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  {[
                    "S.No",
                    "Emp ID",
                    "State",
                    "Employee Name",
                    "Designation",
                    "HQ",
                    "Division",
                    "DOJ",
                    "DOR",
                    "Manager",
                    "Apr POB",
                    "May POB",
                    "Jun POB",
                    "Jul POB",
                    "Aug POB",
                    "Sep POB",
                    "Oct POB",
                    "Nov POB",
                    "Dec POB",
                    "Jan POB",
                    "Feb POB",
                    "Mar POB",
                    "Total POB",
                  ].map((h, i) => (
                    <th key={i} className="border px-2 py-1">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {tableData.map((row, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="border px-2 py-1 text-center">
                      {index + 1}
                    </td>
                    <td className="border px-2 py-1">{row.employee}</td>
                    <td className="border px-2 py-1">{row.state}</td>
                    <td className="border px-2 py-1">{row.employee_name}</td>
                    <td className="border px-2 py-1">{row.designation}</td>
                    <td className="border px-2 py-1">{row.headquarter}</td>
                    <td className="border px-2 py-1">{row.division}</td>
                    <td className="border px-2 py-1">
                      {formatDate(row.final_joining_date) || "-"}
                    </td>
                    <td className="border px-2 py-1">
                      {formatDate(row.resign_date) || "-"}
                    </td>
                    <td className="border px-2 py-1">{row.manager || "-"}</td>

                    <td className="border px-2 py-1">
                      {row.apr_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">{row.may_pob || 0}</td>
                    <td className="border px-2 py-1">
                      {row.jun_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.jul_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.aug_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.sep_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.oct_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.nov_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.dec_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.jan_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.feb_pob_achievement || 0}
                    </td>
                    <td className="border px-2 py-1">
                      {row.mar_pob_achievement || 0}
                    </td>

                    <td className="border px-2 py-1 font-semibold">
                      {row.pob || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex gap-4 justify-center">
              <CustomButton
                onClick={downloadAndPrintTableSheet}
                label="Print"
                variant="print"
              >
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseFf;
